// src/components/AppShell.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  ChatBubbleOutline,
  ChatBubble,
  ExploreOutlined,
  Explore,
  FavoriteBorderOutlined,
  Favorite,
  DynamicFeedOutlined,
  DynamicFeed,
  MapOutlined,
  Map as MapIcon,
  MenuOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { Sidebar } from "./sidebar/Sidebar";
import { NotificationBell } from "./NotificationBell";
import { LocationStatusBadge } from "./LocationStatusBadge";
import { LanguageFlagButton } from "./topbar/LanguageFlagButton";
import { PendingQuestionsButton } from "./PendingQuestionsButton";
import { DiscoverSettingsSheet } from "./discover/DiscoverSettingsSheet";
import { Logo } from "./Logo";
import { useGuideManualRefresh } from "../hooks/useGuide";
import { useT } from '../i18n/useT';

interface AppShellProps {
  children: React.ReactNode;
  /** null for "new" conversation, string for existing */
  currentConversationId: string | null;
}

type TabKey = "chat" | "guide" | "discover" | "map" | "feed" | "none";

const RAIL_WIDTH = 72;

const getTabFromPath = (pathname: string): TabKey => {
  if (pathname.startsWith("/guide")) return "guide";
  if (pathname.startsWith("/discover")) return "discover";
  if (pathname.startsWith("/map")) return "map";
  if (pathname.startsWith("/feed")) return "feed";
  // Legacy /postcards URLs from before the rename — treat as Feed.
  // Old bookmarks still land somewhere sensible.
  if (pathname.startsWith("/postcards")) return "feed";
  // Profile pages (/u/{handle}) and other routes outside the tab
  // system — no tab should appear active. The 'none' value falls
  // through every `activeTab === <tab>` check so the bottom nav
  // shows nothing highlighted.
  if (pathname.startsWith("/u/")) return "none";
  return "chat";
};

export const AppShell: React.FC<AppShellProps> = ({
  children,
  currentConversationId,
}) => {
  const t = useT();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const refreshGuide = useGuideManualRefresh();

  const activeTab = getTabFromPath(routerLocation.pathname);
  // Hamburger menu is available on every tab. It opens the same
  // sidebar drawer (conversation list + user menu). On chat the
  // primary use is jumping between conversations; on other tabs it's
  // the user menu and recent activity.
  const showMenuButton = true;

  // Close drawer when route changes on mobile
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [routerLocation.pathname, isMobile]);

  const goTab = (tab: TabKey) => {
    if (tab === "none") return;
    if (tab === "chat") {
      navigate(
        currentConversationId ? `/chat/${currentConversationId}` : "/chat"
      );
    } else {
      navigate(`/${tab}`);
    }
  };

  const handleSelectConversation = (id: string) => {
    navigate(`/chat/${id}`);
    if (isMobile) setDrawerOpen(false);
  };

  const handleNewConversation = (id: string) => {
    navigate(`/chat/${id}`);
    if (isMobile) setDrawerOpen(false);
  };

  const navItems: {
    key: TabKey;
    label: string;
    icon: React.ReactElement;
    activeIcon: React.ReactElement;
  }[] = [
    {
      key: "chat",
      label: t("Chats"),
      icon: <ChatBubbleOutline />,
      activeIcon: <ChatBubble />,
    },
    {
      key: "guide",
      label: t("Guide"),
      icon: <ExploreOutlined />,
      activeIcon: <Explore />,
    },
    {
      key: "discover",
      label: t("Discover"),
      icon: <FavoriteBorderOutlined />,
      activeIcon: <Favorite />,
    },
    {
      key: "map",
      label: t("Map"),
      icon: <MapOutlined />,
      activeIcon: <MapIcon />,
    },
    {
      key: "feed",
      label: t("Feed"),
      icon: <DynamicFeedOutlined />,
      activeIcon: <DynamicFeed />,
    },
  ];

  // Frosted-glass bars: header and bottom nav are absolute-positioned
  // over the content column with `backdrop-filter: blur` and a
  // translucent background. Content scrolls behind them, picking up
  // the iOS / visionOS aesthetic of "open window into content."
  //
  // CSS variables `--app-header-h` and `--app-bottom-nav-h` are
  // exposed on the column so each page can apply them as
  // padding-top / padding-bottom on its own scroll container. That
  // keeps first/last items visible while letting middle content
  // pass under the bars.
  //
  // Pages that use the variables today:
  //   • src/pages/FeedPage.tsx
  //   • src/components/ChatInterface.tsx (messages list + composer)
  //
  // Pages that should adopt the same pattern (TODO):
  //   • src/pages/ProfilePage.tsx
  //   • src/pages/DiscoverPage.tsx
  //   • src/pages/GuidePage.tsx
  //   • src/pages/MapPage.tsx (likely full-bleed — no padding needed,
  //     the floating bars feel right over a map)
  //
  // The pattern: any scroll container inside `children` that wants
  // content to pass under the bars should add:
  //     paddingTop: 'var(--app-header-h)'
  //     paddingBottom: 'var(--app-bottom-nav-h)'

  // Frosted-glass bar styling. Notes for tuning:
  //   • Background alpha: 0.50 — low enough that content visibly
  //     bleeds through; high enough that text on the bar stays
  //     readable. iOS uses 0.72-0.80 but on a saturated wallpaper;
  //     on near-white app content we go more transparent so the
  //     blur is actually perceptible.
  //   • Blur: 30px — strong enough to read as "frosted glass"
  //     rather than "slightly transparent." iOS uses ~20px.
  //   • Saturation: 200% — punches color out of the underlying
  //     content so when a colorful card scrolls under, the bar
  //     picks up the tint distinctly.
  //   • Border: rgba 0.12 — visible enough to define the bar edge
  //     against light content. (Was 0.06 — too subtle on white.)
  const FROSTED_BG = 'rgba(255, 255, 255, 0.50)';
  const FROSTED_BG_DARK = 'rgba(28, 28, 30, 0.50)';
  const FROSTED_BORDER = 'rgba(0, 0, 0, 0.12)';
  const FROSTED_BORDER_DARK = 'rgba(255, 255, 255, 0.10)';
  const FROSTED_BLUR = 'saturate(200%) blur(30px)';
  const isDark = theme.palette.mode === 'dark';

  const header = (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        pt: 'env(safe-area-inset-top, 0px)',
        minHeight: 'calc(56px + env(safe-area-inset-top, 0px))',
        // Use `backgroundColor` (specific) rather than `background`
        // (shorthand) — sx is more reliable for the specific form.
        backgroundColor: isDark ? FROSTED_BG_DARK : FROSTED_BG,
        backdropFilter: FROSTED_BLUR,
        WebkitBackdropFilter: FROSTED_BLUR,
        borderBottom: `1px solid ${
          isDark ? FROSTED_BORDER_DARK : FROSTED_BORDER
        }`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 0,
          py: 1,
          width: '100%',
        }}
      >
        {showMenuButton && (
          <Tooltip title={activeTab === 'chat' ? 'Conversations' : 'Menu'}>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              size="medium"
              edge="start"
              aria-label={
                activeTab === 'chat' ? 'Open conversations' : 'Open menu'
              }
            >
              <MenuOutlined />
            </IconButton>
          </Tooltip>
        )}
        <Logo
          size={24}
          alt={t('Instant Tours')}
          sx={{ ml: 0.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }} />
        <LanguageFlagButton />
        <LocationStatusBadge onClick={() => setSettingsOpen(true)} />
        <PendingQuestionsButton />
        <NotificationBell />
      </Box>
    </Box>
  );

  const rail = (
    <Stack
      sx={{
        width: RAIL_WIDTH,
        height: "100%",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        alignItems: "center",
        py: 2,
        flexShrink: 0,
      }}
      spacing={1}
    >
      <Box sx={{ mb: 1 }}>
        <Logo size={22} alt={t("Instant Tours")} />
      </Box>
      {navItems.map((item) => {
        const active = activeTab === item.key;
        return (
          <Tooltip key={item.key} title={item.label} placement="right">
            <IconButton
              onClick={() => goTab(item.key)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                color: active ? "primary.main" : "text.secondary",
                bgcolor: active ? "action.selected" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
            >
              {active ? item.activeIcon : item.icon}
            </IconButton>
          </Tooltip>
        );
      })}
      <Box sx={{ flex: 1 }} />
      <Tooltip title={t("Settings")} placement="right">
        <IconButton
          onClick={() => navigate("/settings")}
          sx={{ color: "text.secondary" }}
          aria-label={t("Settings")}
        >
          <SettingsOutlined />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const bottomNav = (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        pb: 'env(safe-area-inset-bottom, 0px)',
        minHeight: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        backgroundColor: isDark ? FROSTED_BG_DARK : FROSTED_BG,
        backdropFilter: FROSTED_BLUR,
        WebkitBackdropFilter: FROSTED_BLUR,
        borderTop: `1px solid ${
          isDark ? FROSTED_BORDER_DARK : FROSTED_BORDER
        }`,
      }}
    >
      <BottomNavigation
        value={activeTab}
        onChange={(_, v) => goTab(v as TabKey)}
        showLabels
        sx={{
          background: 'transparent',
          height: 64,
        }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.key}
            value={item.key}
            label={item.label}
            icon={activeTab === item.key ? item.activeIcon : item.icon}
          />
        ))}
      </BottomNavigation>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        // Fallback for browsers that don't support dvh
        "@supports not (height: 100dvh)": {
          height: "100vh",
        },
        overflow: "hidden",
      }}
    >
      {/* Desktop rail */}
      {!isMobile && rail}

      {/* Conversations drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
      >
        <Sidebar
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onNewConversation={handleNewConversation}
          onSelectConversation={handleSelectConversation}
          currentConversationId={currentConversationId}
        />
      </Drawer>

      {/* Main column. `position: relative` anchors the absolute
          floating bars; `--app-header-h` and `--app-bottom-nav-h`
          let pages compute their own scroll-container padding so
          first/last items aren't hidden under the bars. */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minWidth: 0,
          overflow: 'hidden',
          '--app-header-h':
            'calc(56px + env(safe-area-inset-top, 0px))',
          '--app-bottom-nav-h': isMobile
            ? 'calc(64px + env(safe-area-inset-bottom, 0px))'
            : '0px',
        }}
      >
        <Box sx={{ height: '100%' }}>{children}</Box>
        {header}
        {isMobile && bottomNav}
      </Box>

      {/* Discover settings sheet — opened by tapping LocationStatusBadge.
          Lives at AppShell level so it's reachable from any tab. */}
      <DiscoverSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onRadiusCommit={() => {
          // Re-trigger a Live Guide refresh so the new radius takes
          // effect immediately. The function's own throttle handles
          // double-fires from rapid slider commits.
          refreshGuide();
        }}
      />
    </Box>
  );
};
